import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { GRAPHQL_AUTH_MODE } from '@aws-amplify/api-graphql';
import { getGQLAPI } from '@mcn-platform/models';

const KNOWLEDGE_BASE_ROWS_QUERY = /* GraphQL */ `
  query KnowledgeBaseRows($objectType: String!, $objectID: ID!, $organizationID: ID!) {
    knowledgeBaseRows(objectType: $objectType, objectID: $objectID, organizationID: $organizationID) {
      id
      chunkIndex
      chunkText
      metadata
      skus
    }
  }
`;

type KnowledgeBaseRow = {
  id?: string | null;
  chunkIndex?: number | null;
  chunkText?: string | null;
  metadata?: unknown;
  skus?: unknown;
};

type KnowledgeBaseTabProps = {
  objectType: string;
  objectID: string;
  organizationID: string;
  emptyMessage?: string;
  /** Triggers DynamoDB update to fire stream and reindex. Called when Reindex is clicked. */
  onReindex: () => Promise<void>;
};

const KnowledgeBaseTab = ({
  objectType,
  objectID,
  organizationID,
  onReindex,
  emptyMessage = 'No knowledge base chunks found. Chunks are populated when content is indexed.',
}: KnowledgeBaseTabProps) => {
  const [rows, setRows] = useState<KnowledgeBaseRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reindexing, setReindexing] = useState(false);

  const loadRows = useCallback(async () => {
    if (!organizationID || !objectID) return;
    setLoading(true);
    setError(null);
    try {
      const result: any = await getGQLAPI().graphql({
        query: KNOWLEDGE_BASE_ROWS_QUERY,
        variables: { objectType, objectID, organizationID },
        authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
      });
      const errs = result?.errors;
      if (errs?.length) {
        const msg = errs.map((e: any) => e.message || e.errorType).join('; ');
        setError(msg);
        setRows([]);
        return;
      }
      const items = result?.data?.knowledgeBaseRows ?? [];
      setRows(Array.isArray(items) ? items : []);
    } catch (err: any) {
      const msg = err?.message || err?.errors?.[0]?.message || String(err);
      setError(msg);
      setRows([]);
      console.error('Error loading knowledge base rows:', err);
    } finally {
      setLoading(false);
    }
  }, [organizationID, objectID, objectType]);

  const handleReindex = useCallback(async () => {
    setReindexing(true);
    setError(null);
    try {
      await onReindex();
      await loadRows();
    } catch (err: any) {
      const msg = err?.message || err?.errors?.[0]?.message || String(err);
      setError(msg);
      console.error('Error reindexing:', err);
    } finally {
      setReindexing(false);
    }
  }, [onReindex, loadRows]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  if (loading) {
    return <Typography>Loading chunks...</Typography>;
  }

  if (error) {
    return (
      <Typography color="error" sx={{ mb: 1 }}>
        {error}
      </Typography>
    );
  }

  if (rows.length === 0) {
    return (
      <Box>
        <Button startIcon={<RefreshIcon />} onClick={handleReindex} disabled={reindexing} size="small" sx={{ mb: 2 }}>
          {reindexing ? 'Reindexing...' : 'Reindex'}
        </Button>
        <Typography color="text.secondary">{emptyMessage}</Typography>
      </Box>
    );
  }

  const firstRow = rows[0];
  const itemFields = firstRow ? { metadata: firstRow.metadata, skus: firstRow.skus } : null;

  return (
    <Box>
      <Button startIcon={<RefreshIcon />} onClick={handleReindex} disabled={reindexing} size="small" sx={{ mb: 2 }}>
        {reindexing ? 'Reindexing...' : 'Reindex'}
      </Button>
      <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
        <Table stickyHeader size="small" sx={{ '& .MuiTableCell-root': { borderBottom: '1px solid' } }}>
          <TableHead>
            <TableRow sx={{ '& th': { borderBottom: '2px solid', borderColor: 'divider' } }}>
              <TableCell>Chunk</TableCell>
              <TableCell>Content</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r, idx) => (
              <TableRow
                key={r.id ?? idx}
                sx={{
                  '& td': { borderBottom: '1px solid', borderColor: 'divider' },
                  backgroundColor: idx % 2 === 1 ? 'grey.50' : undefined,
                }}
              >
                <TableCell sx={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                  {r.chunkIndex != null ? r.chunkIndex : idx + 1}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {r.chunkText ?? '-'}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {itemFields && (itemFields.metadata != null || itemFields.skus != null) && (
        <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Item fields (metadata, skus — same for all chunks)
          </Typography>
          {itemFields.metadata != null && (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                metadata
              </Typography>
              <Box component="pre" sx={{ fontSize: '0.8125rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0 }}>
                {typeof itemFields.metadata === 'object'
                  ? JSON.stringify(itemFields.metadata, null, 2)
                  : String(itemFields.metadata)}
              </Box>
            </>
          )}
          {itemFields.skus != null && (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, mb: 0.5 }}>
                skus
              </Typography>
              <Box component="pre" sx={{ fontSize: '0.8125rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0 }}>
                {typeof itemFields.skus === 'object'
                  ? JSON.stringify(itemFields.skus, null, 2)
                  : String(itemFields.skus)}
              </Box>
            </>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default KnowledgeBaseTab;
